export interface GradeRecord {
  section_id: string;
  marking_period_id?: string; // added missing field from in-progress-grades.csv
  course_name?: string; // cached name to display without additional lookups
  grade: number;
  date: string; // ISO 8601, ingestion timestamp
  is_real_course?: boolean;
  grade_slot?: string;
  school_year?: string; // e.g. "2024-2025"; derived from `date` since Aspen's in-progress export doesn't carry it
}

/**
 * Derives a school year label (e.g. "2025-2026") from an ISO date, using
 * a July 1 cutoff: dates before July belong to the year that is ending,
 * dates on/after July 1 belong to the year that is starting.
 */
export function computeSchoolYear(isoDate: string): string {
  const [yearStr, monthStr] = isoDate.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  if (!Number.isFinite(year) || !Number.isFinite(month)) {
    throw new Error(`computeSchoolYear: invalid ISO date "${isoDate}"`);
  }
  const startYear = month >= 7 ? year : year - 1;
  return `${startYear}-${startYear + 1}`;
}

/**
 * The school year of a sitting that a source file labels by its *spring*.
 *
 * DESE's MCAS export names a year by the spring it happened in: `adminyear`
 * `2025` and the suffixed history columns `escaleds2024` / `esgp2024` all mean
 * the testing window in the spring of that calendar year, which is the *second*
 * half of a school year. So spring 2025 is school year 2024-2025.
 *
 * This exists as one function with one test because getting it wrong mislabels
 * an entire year of scores — every record lands under the neighbouring year,
 * silently, and nothing downstream can tell. Delegates to `computeSchoolYear`
 * rather than doing its own arithmetic, so the July 1 cutoff is stated once.
 */
export function springYearToSchoolYear(springYear: number | string): string {
  const year = Number(springYear);
  if (!Number.isInteger(year) || year < 1900 || year > 2200) {
    throw new Error(`springYearToSchoolYear: invalid spring year "${springYear}"`);
  }
  // Any spring month works; May is the MCAS window and reads as intent.
  return computeSchoolYear(`${year}-05-01`);
}

/**
 * Identifies the course-enrollment an entry belongs to, ignoring `term`.
 *
 * The unit almost everything outside a grade display cares about is the
 * *course*, not the semester: credits are granted per course-year, and "failed
 * two courses" must not read as four because both semesters of each were
 * failing. Group by this before counting.
 */
export function transcriptCourseYearKey(entry: {
  section_id: string;
  school_year: string;
}): string {
  return `${entry.section_id}|${entry.school_year}`;
}

/**
 * Groups entries by course-enrollment, preserving first-seen order.
 */
export function groupTranscriptByCourseYear(
  entries: TranscriptEntry[],
): Map<string, TranscriptEntry[]> {
  const groups = new Map<string, TranscriptEntry[]>();
  for (const entry of entries) {
    const key = transcriptCourseYearKey(entry);
    const list = groups.get(key);
    if (list) list.push(entry);
    else groups.set(key, [entry]);
  }
  return groups;
}

/** The school year containing `today` (defaults to now), same July 1 cutoff. */
export function currentSchoolYear(today: Date = new Date()): string {
  return computeSchoolYear(today.toISOString().slice(0, 10));
}

/**
 * The period one transcript grade covers.
 *
 * Aspen reports a grade per *slot* per course-enrollment, and the slots carry
 * two different kinds of information depending on the school:
 *
 *   - The HS models a semester as its own course, and the slot beside the
 *     `Final Grade` says *which semester that course ran in* — it is a term
 *     marker, not a second grade. Verified against the 2025-2026 export: every
 *     course-enrollment named "... A" carries only `S1Grade` and every "... B"
 *     only `S2Grade`, with no exceptions, and 1,730 more with no A/B suffix
 *     also carry exactly one semester slot. For those the slot is the *only*
 *     record of when the course ran, which is why it is worth keeping: it makes
 *     "how does this student do in the spring?" answerable.
 *   - The MS runs full-year courses and reports `S1Grade` and `S2Grade` with no
 *     `Final Grade` at all. There the slots are two real, frequently different
 *     grades for one course, so they become two entries.
 *
 * `FY` therefore means a genuine full-year grade — a `Final Grade` with either
 * no semester slot or both of them — rather than "we didn't look".
 *
 * Absent on entries written before this field existed, where the period cannot
 * be recovered, which is why it is optional rather than defaulted.
 */
export type TranscriptTerm = "FY" | "S1" | "S2";

/** Aspen's grade-slot names, mapped to the period each one covers. */
export const TRANSCRIPT_SLOT_TERMS: Record<string, TranscriptTerm> = {
  "Final Grade": "FY",
  S1Grade: "S1",
  S2Grade: "S2",
};

/** Display order for the terms of one course-year. */
export const TRANSCRIPT_TERM_ORDER: TranscriptTerm[] = ["FY", "S1", "S2"];

export const TRANSCRIPT_TERM_LABELS: Record<TranscriptTerm, string> = {
  FY: "Full year",
  S1: "Semester 1",
  S2: "Semester 2",
};

/** Compact form, for a tag that sits inline beside a course name. */
export const TRANSCRIPT_TERM_SHORT_LABELS: Record<TranscriptTerm, string> = {
  FY: "FY",
  S1: "S1",
  S2: "S2",
};

export interface TranscriptEntry {
  section_id: string;
  course_name: string;
  grade: string; // The grade for this course-enrollment and `term`
  /**
   * Which period `grade` covers. A full-year MS course contributes one entry
   * per semester, so `section_id` + `school_year` alone does not identify an
   * entry — use `transcriptCourseYearKey`.
   */
  term?: TranscriptTerm;
  school_year: string; // e.g. "2024-2025"
  course_gpa_indicator: boolean; // Whether this course counts toward GPA
  course_department: string;
  course_number: string;
  course_description: string;
  course_grade_level: string; // e.g. "9-10", "9-12"
  course_academic_level: string; // e.g. "Stand-New", "Honors", "AP"
  school_id: string;
}

export interface StudentGrades {
  student_id: string;
  grades: GradeRecord[];
}

export interface GPARecord {
  student_id: string;
  simple_gpa?: number;
  weighted_gpa?: number;
  school_id: string;
}

/* Firestore paths for grade documents */
export interface GradePaths {
  "students/{student_id}/grades/{doc_id}": StudentGrades;
}
